import { useState } from "react";
import { CheckCircle2 } from "lucide-react";
import { FadeUp, SectionHeading } from "../components/motion";
import { Honeypot, useGuard, submitForm } from "../components/forms";

const SETTINGS = ["Hospice", "Healthcare / Hospital", "Military", "Public Safety / Police / Fire", "Corporate", "Community / Corrections", "Education", "Other", "Exploring chaplaincy / not currently a chaplain"];
const DURATIONS = [
  { v: "shorter_retreat", l: "Shorter retreat (3-5 days)" },
  { v: "longer_retreat", l: "Longer retreat" },
  { v: "flexible", l: "Flexible" },
];
const REGIONS = [
  { v: "california_west_coast", l: "California / West Coast" },
  { v: "florida_east_coast", l: "Florida / East Coast" },
  { v: "other", l: "Other" },
  { v: "flexible", l: "Flexible" },
];
const TRAVEL = [
  { v: "solo_cabin", l: "Solo cabin" },
  { v: "shared_cabin", l: "Shared cabin" },
  { v: "spouse_companion", l: "With spouse / companion" },
  { v: "undecided", l: "Undecided" },
];
const INTERESTS = [
  { v: "rest", l: "Rest" },
  { v: "spiritual_renewal", l: "Spiritual renewal" },
  { v: "writing_a_book", l: "Writing a book" },
  { v: "fellowship", l: "Fellowship" },
  { v: "professional_opportunities", l: "Professional opportunities" },
  { v: "learning_about_chaplaincy", l: "Learning about chaplaincy" },
];
const CLASSES = [
  { v: "class_caring_for_the_caregiver", l: "Caring for the Caregiver" },
  { v: "class_finding_stillness_at_sea", l: "Finding Stillness at Sea" },
  { v: "class_your_story_matters", l: "Your Story Matters: Begin Your Book" },
  { v: "class_legacy_journaling", l: "Legacy and Spiritual Journaling" },
  { v: "class_exploring_chaplaincy", l: "Exploring Chaplaincy" },
  { v: "class_sunset_gratitude_circle", l: "Sunset Gratitude Circle" },
];

const SAILINGS = [
  { v: "renewal", l: "Chaplain Renewal at Sea" },
  { v: "exploring", l: "Exploring Chaplaincy at Sea" },
  { v: "remembrance", l: "From Diagnosis to Goodbye: Grief and Remembrance at Sea" },
  { v: "not_sure", l: "Not sure yet" },
];

const EMPTY = {
  name: "", email: "", city_state: "", chaplaincy_setting: "",
  preferred_duration: "", departure_region: "", travel_preference: "",
  sailing: "", interests: [], comments: "", consent_updates: false,
};

function RadioRow({ legend, name, options, value, onChange, testBase }) {
  return (
    <fieldset>
      <legend className="field-label text-navy/70">{legend} *</legend>
      <div className="mt-2 flex flex-wrap gap-x-5 gap-y-2">
        {options.map((o) => (
          <label key={o.v} className="flex cursor-pointer items-center gap-2 text-sm text-navy/85">
            <input
              type="radio"
              name={name}
              value={o.v}
              checked={value === o.v}
              onChange={() => onChange(o.v)}
              data-testid={testBase + "-" + o.v}
              className="h-4 w-4 accent-[#B89047]"
            />
            {o.l}
          </label>
        ))}
      </div>
    </fieldset>
  );
}

export default function InterestSection() {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const guard = useGuard();

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const toggleInterest = (v) =>
    setForm((f) => ({
      ...f,
      interests: f.interests.includes(v) ? f.interests.filter((x) => x !== v) : [...f.interests, v],
    }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name.trim() || !form.email.trim() || !form.city_state.trim() || !form.chaplaincy_setting ||
        !form.preferred_duration || !form.departure_region || !form.travel_preference || !form.sailing) {
      setError("Please complete all required fields.");
      return;
    }
    if (!form.consent_updates) {
      setError("Please check the consent box so we can send you updates.");
      return;
    }
    setBusy(true);
    const res = await submitForm("/interest/retreat", {
      ...form,
      started_at: guard.startedAt,
      website: guard.honeypot,
    });
    setBusy(false);
    if (res.ok) setDone(true);
    else setError(res.error);
  };

  return (
    <section
      id="interest"
      data-testid="section-interest"
      className="grain relative overflow-hidden bg-navy py-24 text-cream sm:py-32"
    >
      <div className="mx-auto grid max-w-7xl gap-14 px-5 sm:px-8 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <SectionHeading
            eyebrow="Join the Interest List"
            titleLines={["Help shape the", "inaugural retreat."]}
            intro="Registration is not open and no payment is needed. Share your preferences, and we will share updates as plans are confirmed."
          />
        </div>

        <div className="lg:col-span-7">
          <FadeUp delay={0.15}>
            {done ? (
              <div className="border border-gold/50 bg-navy-abyss/60 p-9" data-testid="retreat-success">
                <CheckCircle2 size={30} strokeWidth={1.25} className="text-gold-light" />
                <p className="mt-5 font-serif text-2xl font-light leading-snug text-cream">
                  Thank you for your interest in Chaplain at Sea.
                </p>
                <p className="mt-4 text-sm leading-relaxed text-cream/75">
                  Your preferences will help shape our inaugural retreat. We&rsquo;ll share updates
                  as plans are confirmed.
                </p>
              </div>
            ) : (
              <form onSubmit={onSubmit} noValidate className="border border-gold/30 bg-cream p-7 text-navy sm:p-9" data-testid="retreat-form">
                <Honeypot value={guard.honeypot} onChange={guard.setHoneypot} />
                <div className="grid gap-5 sm:grid-cols-2">
                  <div>
                    <label htmlFor="ri-name" className="field-label text-navy/70">Name *</label>
                    <input id="ri-name" required data-testid="retreat-name-input" className="field-input mt-1.5" value={form.name} onChange={set("name")} />
                  </div>
                  <div>
                    <label htmlFor="ri-email" className="field-label text-navy/70">Email *</label>
                    <input id="ri-email" type="email" required data-testid="retreat-email-input" className="field-input mt-1.5" value={form.email} onChange={set("email")} />
                  </div>
                </div>
                <div className="mt-5 grid gap-5 sm:grid-cols-2">
                  <div>
                    <label htmlFor="ri-city" className="field-label text-navy/70">City / State *</label>
                    <input id="ri-city" required data-testid="retreat-city-input" className="field-input mt-1.5" value={form.city_state} onChange={set("city_state")} placeholder="e.g. San Diego, CA" />
                  </div>
                  <div>
                    <label htmlFor="ri-setting" className="field-label text-navy/70">Chaplaincy setting *</label>
                    <select id="ri-setting" required data-testid="retreat-setting-select" className="field-input mt-1.5" value={form.chaplaincy_setting} onChange={set("chaplaincy_setting")}>
                      <option value="" disabled>Select a setting</option>
                      {SETTINGS.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                </div>
                <div className="mt-6 grid gap-6">
                  <RadioRow legend="Preferred duration" name="preferred_duration" options={DURATIONS} value={form.preferred_duration} onChange={(v) => setForm((f) => ({ ...f, preferred_duration: v }))} testBase="retreat-duration" />
                  <RadioRow legend="Preferred departure region" name="departure_region" options={REGIONS} value={form.departure_region} onChange={(v) => setForm((f) => ({ ...f, departure_region: v }))} testBase="retreat-region" />
                  <RadioRow legend="Travel preference" name="travel_preference" options={TRAVEL} value={form.travel_preference} onChange={(v) => setForm((f) => ({ ...f, travel_preference: v }))} testBase="retreat-travel" />
                  <RadioRow legend="Sailing that interests you" name="sailing" options={SAILINGS} value={form.sailing} onChange={(v) => setForm((f) => ({ ...f, sailing: v }))} testBase="retreat-sailing" />
                </div>
                <fieldset className="mt-6">
                  <legend className="field-label text-navy/70">Interests</legend>
                  <div className="mt-2 grid gap-2 sm:grid-cols-2">
                    {INTERESTS.map((o) => (
                      <label key={o.v} className="flex cursor-pointer items-center gap-2.5 text-sm text-navy/85">
                        <input type="checkbox" checked={form.interests.includes(o.v)} onChange={() => toggleInterest(o.v)} data-testid={"retreat-interest-" + o.v} className="h-4 w-4 accent-[#B89047]" />
                        {o.l}
                      </label>
                    ))}
                  </div>
                  <p className="mt-3 text-xs leading-relaxed text-navy/55" data-testid="retreat-interests-note">
                    Checking &ldquo;professional opportunities&rdquo; here expresses a topic preference
                    only; it does not authorize sharing information with CCCHAPS.
                  </p>
                </fieldset>
                <fieldset className="mt-6">
                  <legend className="field-label text-navy/70">Planned classes that appeal to you (optional)</legend>
                  <div className="mt-2 grid gap-2 sm:grid-cols-2">
                    {CLASSES.map((c) => (
                      <label key={c.v} className="flex cursor-pointer items-start gap-2.5 text-sm text-navy/85">
                        <input
                          type="checkbox"
                          checked={form.interests.includes(c.v)}
                          onChange={() => toggleInterest(c.v)}
                          data-testid={"retreat-class-" + c.v}
                          className="mt-0.5 h-4 w-4 shrink-0 accent-[#B89047]"
                        />
                        <span>{c.l}</span>
                      </label>
                    ))}
                  </div>
                  <p className="mt-3 text-xs leading-relaxed text-navy/55" data-testid="retreat-classes-note">
                    Proposed offerings for the inaugural retreat — your preferences help shape the program. Participation is optional.
                  </p>
                </fieldset>
                <div className="mt-5">
                  <label htmlFor="ri-comments" className="field-label text-navy/70">Comments (optional)</label>
                  <textarea id="ri-comments" rows={3} data-testid="retreat-comments-input" className="field-input mt-1.5 resize-y" value={form.comments} onChange={set("comments")} placeholder="Anything you would like us to know" />
                </div>
                <label className="mt-6 flex items-start gap-3 text-sm leading-relaxed text-navy/85">
                  <input type="checkbox" required checked={form.consent_updates} onChange={(e) => setForm((f) => ({ ...f, consent_updates: e.target.checked }))} data-testid="retreat-consent-checkbox" className="mt-1 h-4 w-4 shrink-0 accent-[#B89047]" />
                  <span>I would like to receive updates about Chaplain at Sea retreats.</span>
                </label>
                {error && (
                  <p role="alert" data-testid="retreat-error" className="mt-5 border border-red-800/30 bg-red-50 px-4 py-3 text-sm text-red-900">
                    {error}
                  </p>
                )}
                <button
                  type="submit"
                  disabled={busy}
                  data-testid="retreat-submit-btn"
                  className="mt-7 inline-flex items-center justify-center bg-gold px-8 py-4 font-mono text-[12px] uppercase tracking-[0.2em] text-navy transition-all duration-300 hover:bg-gold-light disabled:opacity-60"
                >
                  {busy ? "Sending..." : "Join the Interest List"}
                </button>
              </form>
            )}
          </FadeUp>
        </div>
      </div>
    </section>
  );
}
